// Cours CODE 2 ROUES — Ma première circulation A2
// 2 modes : Lecture (mobile-first) + Présentation (plein écran, 1 section = 1 slide)
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Presentation, X, ChevronLeft, ChevronRight, Home,
  Clock, User, Target, Zap, ArrowRight, BookOpen, Printer,
  CheckCircle2, XCircle,
} from 'lucide-react';
import { coursData } from '../../data/cours/premiereCirculationA2';
import useSeo from '../../hooks/useSeo';
import { LightboxProvider, LightboxImage } from '../../components/LightboxGallery';
import { FullscreenOverlay, enterFullscreen } from '../../components/FullscreenMode';
import { Maximize2 } from 'lucide-react';

function Diagram({ text, big = false }) {
  return (
    <div className={`my-4 py-3 px-4 rounded-xl border-2 border-red-500/30 bg-red-500/5 text-center`}>
      <p className={`${big ? 'text-lg md:text-2xl' : 'text-sm sm:text-base md:text-lg'} font-black tracking-[0.06em] text-foreground leading-tight`}
         style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        {text}
      </p>
    </div>
  );
}

function Retenir({ text, big = false }) {
  return (
    <div className="mt-5 p-4 md:p-5 rounded-xl border-l-4 border-red-500 bg-gradient-to-r from-red-500/10 to-transparent">
      <p className="text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-2 flex items-center gap-1.5">
        <Target className="w-3.5 h-3.5" /> À retenir
      </p>
      <p className={`${big ? 'text-lg md:text-xl' : 'text-sm sm:text-base'} font-bold text-foreground leading-snug`}
         style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        {text}
      </p>
    </div>
  );
}

function Section({ section, presentation = false }) {
  const big = presentation;
  const numSize = big ? 'text-8xl md:text-9xl' : 'text-6xl sm:text-7xl';
  const titleSize = big ? 'text-3xl md:text-5xl lg:text-6xl' : 'text-2xl sm:text-3xl md:text-4xl';

  return (
    <div className={`relative ${big ? 'py-6 md:py-8' : 'py-8 sm:py-12'}`}>
      <div className="flex items-start gap-5 mb-5">
        <span className={`${numSize} font-black leading-none text-red-500/25`}
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{section.num}</span>
        <div className="flex-1 pt-1">
          <p className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">{section.kicker}</p>
          <h2 className={`${titleSize} font-extrabold leading-tight tracking-tight text-foreground ${section.image ? 'sr-only' : ''}`}
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{section.title}</h2>
        </div>
      </div>

      {/* IMAGE ILLUSTRATIVE — vignette cliquable → modale plein écran */}
      {section.image && (
        <LightboxImage
          src={section.image}
          alt={section.imageAlt || section.title}
          caption={section.title}
          testId={`section-image-${section.num}`}
        />
      )}

      {section.lead && <p className={`${big ? 'text-xl md:text-2xl' : 'text-base sm:text-lg'} text-foreground/80 leading-relaxed mb-4 italic`}
                          style={{ fontFamily: "'Crimson Text', Georgia, serif" }}>{section.lead}</p>}

      {/* TIMELINE */}
      {section.type === 'timeline' && (
        <div className="space-y-3 my-4">
          {section.steps.map((s, i) => (
            <div key={i} className="flex items-center gap-4 p-4 rounded-lg border border-white/10 bg-card/60" style={{borderLeft:'3px solid #cf2031'}}>
              <span className={`${big ? 'text-3xl md:text-4xl' : 'text-2xl'} font-black text-red-500/70`} style={{fontFamily:"'Playfair Display',serif"}}>{s.num}</span>
              <div className="flex-1">
                <p className={`font-black uppercase tracking-wide text-foreground ${big?'text-base md:text-lg':'text-sm'}`}>{s.title}</p>
                {s.body && <p className={`text-foreground/75 ${big?'text-base md:text-lg':'text-sm'} mt-0.5`}>{s.body}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CONVOY */}
      {section.type === 'convoy' && (
        <div className="my-5 p-5 rounded-xl border-2 border-white/10 bg-card/40">
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5">
            {section.convoy.map((v, i) => (
              <React.Fragment key={i}>
                <div className="text-center">
                  <span className={`${big?'text-6xl md:text-7xl':'text-5xl'}`}>{v.icon}</span>
                  <p className="text-[10px] md:text-xs font-black uppercase tracking-wider text-foreground/80 mt-1">{v.label}</p>
                </div>
                {i < section.convoy.length - 1 && <ChevronRight className="w-5 h-5 md:w-8 md:h-8 text-red-500/60" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      {section.splitMessage && (
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          {[section.splitMessage.left, section.splitMessage.right].map((m, i) => (
            <div key={i} className="p-4 rounded-xl border-2 text-center" style={{borderColor: m.color, background: `${m.color}15`}}>
              <p className={`${big?'text-base md:text-lg':'text-sm'} font-black uppercase tracking-[0.04em]`} style={{color:m.color}}>{m.text}</p>
            </div>
          ))}
        </div>
      )}

      {/* SPLIT SCREEN */}
      {section.type === 'split' && (
        <div className="grid md:grid-cols-2 gap-4 my-4">
          {[section.split.left, section.split.right].map((s, i) => (
            <div key={i} className="p-5 rounded-xl border-2 border-red-500/30 bg-red-500/5">
              <p className={`${big?'text-lg md:text-xl':'text-base'} font-black uppercase tracking-wider text-red-500 mb-3`}>{s.kicker}</p>
              <ul className="space-y-1.5">
                {s.points.map((p, j) => <li key={j} className={`${big?'text-base md:text-lg':'text-sm'} font-bold text-foreground/90`}>• {p}</li>)}
              </ul>
              {s.note && <p className={`mt-3 pt-3 border-t border-white/10 italic text-foreground/70 ${big?'text-base':'text-xs'}`}>{s.note}</p>}
            </div>
          ))}
        </div>
      )}

      {/* RADIO BLOCKS */}
      {section.type === 'radioBlocks' && (
        <div className="grid md:grid-cols-2 gap-4 my-4">
          {[section.radioBlocks.heard, section.radioBlocks.understood].map((b, i) => (
            <div key={i} className="p-5 rounded-xl border border-white/15 bg-card/70" style={{borderLeft: `4px solid ${i===0?'#0ea5e9':'#cf2031'}`}}>
              <p className={`text-[11px] font-black uppercase tracking-wider mb-2 ${i===0?'text-sky-500':'text-red-500'}`}>{b.kicker}</p>
              <p className={`${big?'text-lg md:text-xl':'text-base'} text-foreground italic leading-relaxed`} style={{fontFamily:"'Crimson Text',serif"}}>{b.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* DO / DONT */}
      {section.type === 'doDont' && (
        <div className="grid md:grid-cols-2 gap-4 my-4">
          <div className="p-5 rounded-xl border-2 border-green-500/40 bg-green-500/5">
            <p className="text-[11px] font-black uppercase tracking-wider text-green-500 mb-3 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4"/>{section.doDont.do.kicker}</p>
            <ul className="space-y-1.5">{section.doDont.do.points.map((p,i)=><li key={i} className={`${big?'text-base md:text-lg':'text-sm'} text-foreground/90`}>{p}</li>)}</ul>
          </div>
          <div className="p-5 rounded-xl border-2 border-red-500/40 bg-red-500/5">
            <p className="text-[11px] font-black uppercase tracking-wider text-red-500 mb-3 flex items-center gap-1.5"><XCircle className="w-4 h-4"/>{section.doDont.dont.kicker}</p>
            <ul className="space-y-1.5">{section.doDont.dont.points.map((p,i)=><li key={i} className={`${big?'text-base md:text-lg':'text-sm'} text-foreground/90`}>{p}</li>)}</ul>
          </div>
        </div>
      )}

      {/* STEPS 3 */}
      {section.type === 'steps3' && (
        <div className="grid sm:grid-cols-3 gap-3 my-4">
          {section.steps.map((s, i) => (
            <div key={i} className="p-5 rounded-xl border border-white/10 bg-card/60 text-center" style={{borderTop:'3px solid #cf2031'}}>
              <span className={`${big?'text-4xl md:text-5xl':'text-3xl'} font-black text-red-500/60`} style={{fontFamily:"'Playfair Display',serif"}}>{String(i+1).padStart(2,'0')}</span>
              <p className={`font-black uppercase tracking-wider text-red-500 mt-2 mb-1 ${big?'text-base md:text-lg':'text-sm'}`}>{s.title}</p>
              <p className={`text-foreground/85 leading-snug ${big?'text-base md:text-lg':'text-sm'}`}>{s.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* CARDS 3 */}
      {section.type === 'cards3' && (
        <div className="grid md:grid-cols-3 gap-4 my-4">
          {section.cards.map((c, i) => (
            <div key={i} className="p-5 rounded-xl border border-red-500/30 bg-gradient-to-b from-red-500/5 to-transparent">
              <p className={`text-[11px] font-black uppercase tracking-wider text-red-500 mb-3`}>{c.kicker}</p>
              <p className={`font-black tracking-[0.04em] text-foreground mb-2 ${big?'text-base md:text-lg':'text-sm'}`} style={{fontFamily:"'Playfair Display',serif"}}>{c.diagram}</p>
              <p className={`text-foreground/80 italic leading-relaxed ${big?'text-base md:text-lg':'text-sm'}`} style={{fontFamily:"'Crimson Text',serif"}}>{c.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* METHOD DUAL */}
      {section.type === 'methodDual' && (
        <div className="grid md:grid-cols-2 gap-4 my-4">
          {[section.methodDual.first, section.methodDual.second].map((m, i) => (
            <div key={i} className="p-5 rounded-xl border border-white/10 bg-card/60">
              <p className="text-[11px] font-black uppercase tracking-wider text-red-500 mb-3">{m.kicker}</p>
              <p className={`font-black tracking-[0.04em] text-foreground mb-3 ${big?'text-base md:text-lg':'text-sm'}`} style={{fontFamily:"'Playfair Display',serif"}}>{m.diagram}</p>
              <p className={`text-foreground/85 leading-relaxed ${big?'text-base md:text-lg':'text-sm'}`}>{m.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* THEME CARDS */}
      {section.type === 'themeCards' && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 my-4">
          {section.themeCards.map((c, i) => (
            <div key={i} className="p-4 rounded-xl border border-white/10 bg-card/60 text-center hover:border-red-500/50 transition">
              <span className={`${big?'text-4xl md:text-5xl':'text-3xl'}`}>{c.icon}</span>
              <p className={`font-black uppercase tracking-wider text-foreground mt-2 mb-1 ${big?'text-sm md:text-base':'text-xs'}`}>{c.title}</p>
              <p className={`text-foreground/70 leading-snug ${big?'text-sm md:text-base':'text-xs'}`}>{c.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* BILAN */}
      {section.type === 'bilan' && (
        <>
          <div className="space-y-3 my-4">
            {section.bilan.map((b, i) => (
              <div key={i} className="p-5 rounded-xl border-2 flex items-center gap-4" style={{borderColor:`${b.color}55`, background:`${b.color}0d`}}>
                <span className={`${big?'text-4xl md:text-5xl':'text-3xl'} font-black`} style={{color:b.color}}>{b.icon}</span>
                <div className="flex-1">
                  <p className={`font-black uppercase tracking-wider ${big?'text-base md:text-lg':'text-sm'}`} style={{color:b.color}}>{b.title}</p>
                  <div className="mt-2 h-8 border-b-2 border-dashed border-white/15"></div>
                </div>
              </div>
            ))}
          </div>
          {section.autoEval && <p className="mt-3 text-center text-sm text-foreground/70 italic">{section.autoEval}</p>}
        </>
      )}

      {/* REFLEX 7 */}
      {section.type === 'reflex7' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 my-4">
            {section.reflex.map((r, i) => (
              <div key={i} className="p-4 rounded-xl border border-red-500/25 bg-gradient-to-b from-red-500/8 to-transparent text-center">
                <span className={`${big?'text-4xl md:text-5xl':'text-3xl'}`}>{r.icon}</span>
                <p className={`font-black uppercase tracking-wider text-red-500 mt-2 mb-1 ${big?'text-sm md:text-base':'text-xs'}`}>{r.title}</p>
                <p className={`text-foreground/75 leading-snug ${big?'text-sm md:text-base':'text-xs'}`}>{r.body}</p>
              </div>
            ))}
          </div>
          {section.finalWords && (
            <div className="mt-6 p-6 md:p-8 rounded-xl border-2 border-red-500/40 bg-red-500/10 text-center">
              <p className={`${big?'text-2xl md:text-4xl':'text-lg sm:text-2xl'} font-black leading-tight tracking-tight text-foreground whitespace-pre-line`} style={{fontFamily:"'Playfair Display',serif"}}>{section.finalWords}</p>
            </div>
          )}
        </>
      )}

      {/* Generic diagram + body + list (utilisé par certaines sections) */}
      {section.diagram && !['cards3','methodDual'].includes(section.type) && <Diagram text={section.diagram} big={big} />}
      {section.body && section.body.map((p,i)=><p key={i} className={`${big?'text-lg md:text-xl':'text-sm sm:text-base'} text-foreground/85 leading-relaxed mb-3`} dangerouslySetInnerHTML={{__html:p}} />)}
      {section.list && !['reflex7','themeCards'].includes(section.type) && (
        <div className="flex flex-wrap gap-2 my-3">
          {section.list.map((t,i)=><span key={i} className={`inline-block px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.04] ${big?'text-sm md:text-base':'text-xs'} font-bold text-foreground/85`}>{t}</span>)}
        </div>
      )}

      {section.retenir && <Retenir text={section.retenir} big={big} />}
    </div>
  );
}

function PresentationMode({ slides, onExit }) {
  const [idx, setIdx] = useState(0);
  const total = slides.length;
  const next = useCallback(() => setIdx(i => Math.min(i+1, total-1)), [total]);
  const prev = useCallback(() => setIdx(i => Math.max(i-1, 0)), []);
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onExit();
      else if (['ArrowRight',' ','PageDown'].includes(e.key)) { e.preventDefault(); next(); }
      else if (['ArrowLeft','PageUp'].includes(e.key)) { e.preventDefault(); prev(); }
      else if (e.key === 'Home') setIdx(0);
      else if (e.key === 'End') setIdx(total-1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [next, prev, onExit, total]);
  return (
    <div className="fixed inset-0 z-[9999] bg-background overflow-hidden flex flex-col" data-testid="presentation-mode">
      <div className="h-1 bg-white/5"><div className="h-full bg-red-500 transition-all duration-300" style={{width:`${((idx+1)/total)*100}%`}} /></div>
      <div className="flex-1 overflow-y-auto"><div className="max-w-5xl mx-auto px-6 md:px-10">{slides[idx]}</div></div>
      <div className="shrink-0 border-t border-white/10 bg-background/90 backdrop-blur-md px-4 md:px-6 py-3 flex items-center justify-between gap-3">
        <button onClick={prev} disabled={idx===0} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-card border border-white/10 text-foreground/85 text-sm font-bold hover:border-red-500/50 transition disabled:opacity-30" data-testid="btn-prev-slide"><ChevronLeft className="w-4 h-4"/> Précédent</button>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-black uppercase tracking-widest text-foreground/60" data-testid="slide-counter">{idx+1} <span className="text-foreground/30">/</span> {total}</span>
          <button onClick={onExit} className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg border border-white/10 text-foreground/70 text-sm hover:text-foreground hover:border-white/30 transition" data-testid="btn-exit-presentation"><X className="w-4 h-4"/> Quitter</button>
        </div>
        <button onClick={next} disabled={idx===total-1} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold shadow-md transition disabled:opacity-30" data-testid="btn-next-slide">Suivant <ChevronRight className="w-4 h-4"/></button>
      </div>
    </div>
  );
}

export default function PremiereCirculationA2() {
  const [presenting, setPresenting] = useState(false);
  useSeo({
    title: `${coursData.title} — Cours enseignant/élève`,
    description: `Cours CODE 2 ROUES : 14 chapitres pédagogiques pour la première leçon en circulation A2. Consultable sur mobile, projetable en salle.`,
    path: '/cours/premiere-circulation-a2',
  });

  const heroSlide = (
    <div className="min-h-[70vh] flex flex-col justify-center py-8">
      <p className="text-[11px] md:text-[12px] font-black uppercase tracking-[0.28em] text-red-500 mb-3">Cours enseignant · Support élève</p>
      <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-tight tracking-tight text-foreground mb-4" style={{fontFamily:"'Playfair Display',serif"}}>{coursData.title}</h1>
      <p className="text-lg md:text-2xl text-foreground/80 italic leading-relaxed mb-5" style={{fontFamily:"'Crimson Text',serif"}}>{coursData.subtitle}</p>
      <p className="text-base md:text-xl text-foreground/75 leading-relaxed mb-6">{coursData.heroBody}</p>
      <div className="p-5 rounded-xl border-2 border-red-500/40 bg-red-500/10 text-center">
        <p className="text-[10px] font-black uppercase tracking-[0.28em] text-red-500/80 mb-2">Fil conducteur</p>
        <p className="text-xl md:text-3xl font-black tracking-[0.06em] text-foreground" style={{fontFamily:"'Playfair Display',serif"}}>{coursData.mantra}</p>
      </div>
    </div>
  );

  const slides = [heroSlide, ...coursData.sections.map(s => <Section key={s.num} section={s} presentation />)];

  return (
    <LightboxProvider>
      {presenting ? (
        <FullscreenOverlay onExit={() => setPresenting(false)} />
      ) : (
        <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-foreground/85 text-[12px] font-bold transition" data-testid="btn-home"><Home className="w-4 h-4"/><span className="hidden sm:inline">Accueil</span></Link>
          <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-red-500 truncate">Cours · Circulation A2</span>
          <div className="flex items-center gap-2">
            <button onClick={()=>window.print()} className="hidden sm:inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-white/10 text-foreground/70 text-[12px] font-bold hover:text-foreground hover:border-white/30 transition" data-testid="btn-print"><Printer className="w-4 h-4"/> Imprimer</button>
            <button onClick={()=>enterFullscreen(() => setPresenting(true))} className="inline-flex items-center gap-1.5 px-3 sm:px-4 h-9 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[12px] font-black uppercase tracking-wider shadow-md transition" data-testid="btn-start-presentation"><Maximize2 className="w-4 h-4"/> Plein écran</button>
          </div>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        <header className="py-8 sm:py-12 border-b border-white/10 mb-4">
          <div className="flex items-center gap-2 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-red-500 mb-4"><BookOpen className="w-3.5 h-3.5"/><span>Cours enseignant · Support élève</span></div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight text-foreground mb-3" style={{fontFamily:"'Playfair Display',serif"}}>{coursData.title}</h1>
          <p className="text-lg sm:text-xl text-foreground/70 italic mb-5" style={{fontFamily:"'Crimson Text',serif"}}>{coursData.subtitle}</p>
          <p className="text-base sm:text-lg text-foreground/85 leading-relaxed mb-5">{coursData.heroBody}</p>
          <div className="flex flex-wrap items-center gap-3 sm:gap-5 text-[11px] sm:text-[12px] text-foreground/60 mb-5">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5"/> {coursData.duration}</span>
            <span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5"/> {coursData.author}</span>
            <span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-red-500"/> 14 chapitres</span>
          </div>
          <div className="p-4 sm:p-5 rounded-xl border-2 border-red-500/30 bg-red-500/5 text-center">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.28em] text-red-500/80 mb-2">Fil conducteur</p>
            <p className="text-base sm:text-xl md:text-2xl font-black tracking-[0.04em] text-foreground leading-tight" style={{fontFamily:"'Playfair Display',serif"}}>{coursData.mantra}</p>
          </div>
        </header>

        <div className="divide-y divide-white/10" data-testid="cours-sections">
          {coursData.sections.map(s => <Section key={s.num} section={s} />)}
        </div>

        <section className="mt-10 p-6 sm:p-8 rounded-xl border border-white/10 bg-gradient-to-br from-red-500/10 to-transparent text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.28em] text-red-500 mb-3">Pour aller plus loin</p>
          <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3" style={{fontFamily:"'Playfair Display',serif"}}>Continue avec le livret Circulation</h3>
          <p className="text-sm text-foreground/70 max-w-xl mx-auto mb-5">Coche les 7 chapitres du <strong>livret Circulation A2</strong> au fil de tes séances et retrouve toutes les techniques dans le <strong>Magazine CODE 2 ROUES</strong>.</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Link to="/livret-circulation" className="inline-flex items-center gap-2 px-5 h-11 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[13px] font-bold shadow-md transition" data-testid="btn-livret-circulation">Ouvrir le livret Circulation <ArrowRight className="w-4 h-4"/></Link>
            <Link to="/magazine" className="inline-flex items-center gap-2 px-5 h-11 rounded-lg border border-white/15 text-foreground/85 text-[13px] font-bold hover:border-red-500/50 transition">Explorer le Magazine</Link>
          </div>
        </section>
      </article>
    </div>
      )}
    </LightboxProvider>
  );
}
